import { useEffect } from 'react';
import {
  mixpanelRegisterUser,
  mixpanelReset,
  mixpanelSetUser,
} from '../../common/userTracking';
import { IUserSettings } from '../../recoil/selectors/permissionsSelector';

/**
 * Hook to periodically check and manage Mixpanel storage to prevent localStorage overflow
 * @param userSettings The current user settings
 */
export const useMixpanelManager = (userSettings?: IUserSettings) => {
  const checkAndManageMixpanel = () => {
    try {
      let currentSize = 0;
      for (const key in localStorage) {
        // eslint-disable-next-line
        if (localStorage.hasOwnProperty(key)) {
          // Using length property; each character is roughly one byte in UTF-16 (this is approximate)
          // eslint-disable-next-line
          currentSize += localStorage?.getItem?.(key)?.length || 0;
        }
      }
      const threshold = 4 * 1024 * 1024; // 4MB

      if (currentSize > threshold) {
        // eslint-disable-next-line
        console.warn(
          `localStorage size exceeds threshold (${(
            currentSize /
            (1024 * 1024)
          ).toFixed(2)}MB). Resetting mixpanel...`
        );

        // Only reset mixpanel, not all localStorage
        mixpanelReset();

        // Re-register with mixpanel if user is logged in
        if (userSettings?.userid) {
          mixpanelRegisterUser(userSettings);
          mixpanelSetUser(userSettings);
        }
      }
    } catch (error) {
      // eslint-disable-next-line
      console.error('Error checking localStorage size:', error);
    }
  };

  // Periodic check every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkAndManageMixpanel();
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [userSettings?.userid]); // Only re-run if userid changes

  return { checkAndManageMixpanel };
};
